/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /* POSITY-EDIT (3 sep 2026) — handoff Zoekervaring §3.
     Twee afwijkingen van Horizon:
       - MAX van 4 naar 8. De overlay toont er 4 op desktop en 3 op tablet, maar de rail
         op mobiel scrollt door tot 8.
       - Een bewaartermijn van 30 dagen. Horizon bewaarde onbeperkt; iets dat je zes weken
         geleden zag is geen "laatst bekeken" meer.
     De lijst zelf blijft een array van ID's — dat is bewust. De handoff schreef een eigen
     JSON-store voor met prijs en beeld-URL erin, maar dan staat er een prijs in de browser
     van de bezoeker die na een prijswijziging niet meer klopt. Nu rendert Liquid de kaarten
     server-side (sections/predictive-search.liquid), dus prijs, beeld en beschikbaarheid
     zijn altijd actueel en een verdwenen product valt vanzelf uit de lijst.
     De tijdstempels staan in een APARTE sleutel, zodat het bestaande formaat intact blijft
     en oude browsers met alleen de ID-lijst gewoon blijven werken. */
  /** @static @constant {string} Key met {id: timestamp}, alleen voor de bewaartermijn */
  static #STAMP_KEY = 'viewedProducts:t';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;
  /** @static @constant {number} Bewaartermijn in milliseconden (30 dagen) */
  static #MAX_AGE = 30 * 24 * 60 * 60 * 1000;

  /** @returns {Record<string, number>} */
  static #getStamps() {
    try {
      const raw = localStorage.getItem(this.#STAMP_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    /* Alles in try/catch: iOS privémodus GOOIT een fout op setItem in plaats van stil te
       falen, en dan zou een leesactie hierboven de hele pagina meenemen. Niets kunnen
       onthouden is geen reden om de PDP te breken. */
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

      const stamps = this.#getStamps();
      stamps[productId] = Date.now();
      // De stempels nooit groter laten worden dan de lijst zelf.
      for (const id of Object.keys(stamps)) {
        if (!viewedProducts.includes(id)) delete stamps[id];
      }
      localStorage.setItem(this.#STAMP_KEY, JSON.stringify(stamps));
    } catch (e) {
      /* opslag geweigerd of vol — verder zonder geheugen */
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
      localStorage.removeItem(this.#STAMP_KEY);
    } catch (e) {
      /* zie addProduct */
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];

      /* Verlopen items eruit. Een ID zonder stempel komt uit de tijd vóór deze wijziging;
         die laten we staan in plaats van hem weg te gooien — hij verdwijnt vanzelf zodra
         er acht nieuwere producten bekeken zijn. */
      const stamps = this.#getStamps();
      const now = Date.now();
      const fresh = stored.filter((/** @type {string} */ id) => {
        const seen = stamps[id];
        return !seen || now - seen < this.#MAX_AGE;
      });

      if (fresh.length !== stored.length) {
        try {
          localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(fresh));
        } catch (e) {
          /* alleen opruimen; mislukt dat, dan volgende keer opnieuw */
        }
      }
      return fresh;
    } catch (e) {
      return []; // corrupt of geblokkeerd -> lege lijst, nooit een crash
    }
  }
}
